import { v4 } from "uuid";
import { api } from "../api";
import { config } from "../data/config";
import convertTemplateInAnswer from "./convertTemplateInAnswer";
import { ensureWhatsappFlowLinked } from "./ensureWhatsappFlowLinked";

// Saves of the same scenario must never run in parallel: both runs would see
// shouldBeReplaced=true on the shared nodes and create duplicate templates,
// moving the reply buttons from one to the other (SM-684).
const pendingSaves = new Map();

export async function saveScenarioFlow(...args) {
  const scenarioId = args[2];
  const previousSave = pendingSaves.get(scenarioId) ?? Promise.resolve();
  const currentSave = previousSave
    .catch(() => {})
    .then(() => runSaveScenarioFlow(...args));
  pendingSaves.set(scenarioId, currentSave);
  try {
    return await currentSave;
  } finally {
    if (pendingSaves.get(scenarioId) === currentSave) {
      pendingSaves.delete(scenarioId);
    }
  }
}

async function runSaveScenarioFlow(
  nodes,
  edges,
  scenarioId,
  campaignId,
  user,
  campaignName,
) {
  const forceJsonFormat = (str) => {
    try {
      return JSON.parse(str);
    } catch (e) {
      console.error(e);
    }
  };

  async function processNodeCards(
    node,
    user,
    config,
    scenarioId,
    api,
    officialWhatsappTemplate,
    whatsappTemplateId,
    flowNodeRecord,
  ) {
    if (!node.data?.cards) return;

    let updatedCards = [];
    for (const [index, card] of node.data.cards.entries()) {
      const buttonsCard = card.components.find((c) => c.type === "BUTTONS");
      if (!buttonsCard) {
        updatedCards.push(card);
        continue;
      }

      const quickReplies = buttonsCard.buttons.filter(
        (c) => c.type === "QUICK_REPLY" || c.type === "quick_reply",
      );
      const urlButtons = buttonsCard.buttons.filter(
        (c) => c.type === "URL" || c.type === "url",
      );

      await processQuickRepliesInCards(
        quickReplies,
        user,
        config,
        scenarioId,
        api,
        flowNodeRecord,
      );
      await processUrlButtonsInCards(
        urlButtons,
        index,
        user,
        config,
        api,
        officialWhatsappTemplate,
        whatsappTemplateId,
        flowNodeRecord.id,
      );

      updatedCards.push({ ...card });
    }

    node.data.cards = updatedCards;

    await api.whatsappTemplate.update(whatsappTemplateId, {
      cards: updatedCards,
    });
  }

  async function processQuickRepliesInCards(
    quickReplies,
    user,
    config,
    scenarioId,
    api,
    flowNodeRecord,
  ) {
    if (!quickReplies || quickReplies.length === 0) return;

    for (const quickReply of quickReplies) {
      const id = v4();
      const customField = quickReply.saveAsCustomField
        ? { [quickReply.customField]: quickReply.customFieldValue }
        : null;
      const replyButtonNode = await api.flowNode.create({
        flowNode: {
          customer: { _link: user?.customerId },
          type: "reply_button",
          scenario: { _link: scenarioId },
          parentNode: { _link: flowNodeRecord.id },
          replyButton: {
            create: {
              messageSkeleton: {
                _link: flowNodeRecord.whatsappTemplate.messageSkeleton.id,
              },
              customer: { _link: user?.customerId },
              whatsappId: id,
              title: quickReply.text,
              customField: quickReply.saveAsCustomField ? customField : null,
            },
          },
        },
      });

      if (replyButtonNode?.id) {
        const record = await api.flowNode.findById(replyButtonNode.id, {
          select: {
            id: true,
            replyButton: { id: true },
          },
        });
        quickReply.replyButtonId = record.replyButton.id;
        quickReply.gadgetId = record.id;
      }
    }
  }

  async function processUrlButtonsInCards(
    urlButtons,
    cardIndex,
    user,
    config,
    api,
    officialWhatsappTemplate,
    whatsappTemplateId,
    flowNodeRecordId,
  ) {
    if (!urlButtons) return;

    let officialUrls = null;

    if (officialWhatsappTemplate) {
      officialUrls =
        officialWhatsappTemplate.urlButtonsInCards?.find(
          (c) => c.cardIndex === cardIndex,
        )?.buttons || [];
    }

    for (let i = 0; i < urlButtons.length; i++) {
      const urlButton = urlButtons[i];

      const id = v4();

      if (
        (urlButton.gadgetId ?? "") === "" &&
        !urlButton?.url?.includes("{{1}}")
      ) {
        const urlButtonNode = await api.flowNode.create(
          {
            flowNode: {
              customer: {
                _link: user?.customerId ?? config.customerId,
              },
              type: "url_button",
              scenario: { _link: scenarioId },
              parentNode: { _link: flowNodeRecordId },
              redirection: {
                create: {
                  customer: { _link: user?.customerId ?? config.customerId },
                  url: officialUrls ? officialUrls[i]?.url : urlButton?.url,
                  uuid: id,
                  whatsappTemplateId: whatsappTemplateId,
                },
              },
            },
          },
          {
            select: {
              id: true,
              redirection: {
                id: true,
                uuid: true,
                whatsappTemplateId: true,
                url: true,
              },
            },
          },
        );

        if (urlButtonNode?.redirection?.id) {
          urlButton.gadgetId = urlButtonNode.id;
          urlButton.redirectionUrlId = urlButtonNode?.redirection.id;
          urlButton.redirectionUrlUuid = urlButtonNode?.redirection.uuid;
          urlButton.url = urlButtonNode?.redirection.url;
        }
      }
    }
  }

  async function processUrlButtons(
    urlButtons,
    node,
    user,
    config,
    api,
    flowNodeRecord,
    shouldBeReplaced,
  ) {
    let hasUrlButtons = false;

    for (let i = 0; i < urlButtons.length; i++) {
      const b = urlButtons[i];

      if (b.url?.includes("{{1}}")) {
        continue;
      }

      if (shouldBeReplaced || !b.gadgetId) {
        const urlButtonNode = await api.flowNode.create({
          flowNode: {
            customer: {
              _link: user?.customerId ?? config.customerId,
            },
            type: "url_button",
            scenario: { _link: scenarioId },
            parentNode: { _link: node.data.nodeId },
          },
        });

        const isFirstButton = !hasUrlButtons;

        // b.gadgetId = urlButtonNode.id;
        if (isFirstButton) {
          node.data.urlButton.gadgetId = urlButtonNode.id;
        } else {
          node.data.urlButton2.gadgetId = urlButtonNode.id;
        }

        let redirectionId;
        if (flowNodeRecord) {
          redirectionId = isFirstButton
            ? flowNodeRecord.whatsappTemplate?.redirectionUrl?.id
            : flowNodeRecord.whatsappTemplate?.redirectionUrl2?.id;
        } else {
          redirectionId = isFirstButton
            ? node.data.urlButton?.redirectionUrlId
            : node.data.urlButton2?.redirectionUrlId;
        }

        if (redirectionId) {
          await api.redirection.update(redirectionId, {
            flowNode: { _link: urlButtonNode.id },
          });

          b.redirectionUrlId = redirectionId;
        }

        if (isFirstButton) {
          hasUrlButtons = true;
        }
      }
    }
  }

  async function nodeLoop() {
    let gaiaWebsiteDatasourcesModified = false;
    for (var k = 0; k < nodes.length; k++) {
      const node = nodes[k];
      let urlButtons = [];

      const { urlButtonLink, urlButtonLink2 } = _formatURLButtons(
        node.data,
        user.customer,
      );

      // Known Node
      if (node.data.nodeId ?? "" != "") {
        var nodeType;
        var nodeData;

        if (node.data?.urlButton) {
          urlButtons.push(node.data?.urlButton);
        }
        if (node.data?.urlButton2) {
          urlButtons.push(node.data?.urlButton2);
        }

        if (node.type === "trigger") {
          nodeType = "whatsappTrigger";
          nodeData = {
            id: node.data.whatsappTriggerId,
            type: node.data.type,
            text: node.data.text?.trim(),
          };
        } else if (node.type === "message") {
          nodeType = "messageSkeleton";
          nodeData = _formatNodeData(
            node,
            { urlButtonLink, urlButtonLink2 },
            { update: true },
          );
        } else if (node.type === "template") {
          if (node.data.shouldBeConverted) {
            console.log(
              `Converting template ${node.data.whatsappTemplateId} in Answer`,
            );
            nodeType = "messageSkeleton";
            await convertTemplateInAnswer({
              api,
              node,
              nodeData,
              campaignId,
              urlButtonLink,
              user,
              scenarioId,
              edges,
            });
            continue;
          } else if (node.data.shouldBeReplaced) {
            console.log(`Replacing template ${node.data.whatsappTemplateId}`);
            node.data.buttons = node.data.buttons.map((button) => {
              return {
                title: button.title,
                customField: button.customField,
                saveAsCustomField: button.saveAsCustomField,
                customFieldValue: button.customFieldValue,
              };
            });

            let type;
            let nodeType;
            let nodeData;
            try {
              if (node.data.oldData?.whatsappTemplateId) {
                let buttons = node.data.oldData?.buttons;
                const cards = node.data.oldData?.cards;
                if (cards) {
                  const buttonsInCards = cards.flatMap((card) =>
                    card.components
                      .filter((component) => component.type === "BUTTONS")
                      .flatMap((component) =>
                        component.buttons.filter(
                          (button) =>
                            button.type === "QUICK_REPLY" ||
                            (button.type === "URL" &&
                              !button.url.includes("{{1}}")),
                        ),
                      ),
                  );

                  buttons = [...buttons, ...buttonsInCards];
                }

                const { urlButton: _urlButton, urlButton2: _urlButton2 } =
                  node.data.oldData;

                if (_urlButton) {
                  buttons = [...buttons, _urlButton];
                }
                if (_urlButton2) {
                  buttons = [...buttons, _urlButton2];
                }

                if (buttons?.length) {
                  const buttonsIds = buttons
                    .map(({ gadgetId }) => gadgetId)
                    .filter((i) => !!i);
                  await api.flowNode.bulkDelete(buttonsIds);
                }
              }
            } catch (error) {
              console.error(`Error deleting buttons: ${error}`);
            }
            try {
              if (node.data.nodeId && node.data.shouldBeReplaced) {
                console.log("node to delete", node);
                try {
                  await api.flowNode.delete(node.data.nodeId);
                } catch (e) {
                  console.error(`Unable to delete node ${e}`);
                }
                const buttonNodesToDelete = await api.flowNode.findMany({
                  filter: {
                    type: { in: ["reply_button", "url_button", "flow_button"] },
                    parentNodeId: { equals: node.data.nodeId },
                  },
                  select: { id: true },
                });
                console.log(
                  `Found ${buttonNodesToDelete?.length} button nodes to delete`,
                );
                if (buttonNodesToDelete.length > 0) {
                  await api.flowNode.bulkDelete(
                    buttonNodesToDelete.map((b) => b.id),
                  );
                }
              }
            } catch (error) {
              console.error(`Error deleting button nodes: ${error}`);
            }

            node.data.shouldBeReplaced = false;
            if (!node.data.isEditing) {
              node.data.oldData = null;
            }

            // Création d'un nouveau template
            const officialWhatsappTemplate =
              await api.officialWhatsappTemplate.maybeFindFirst({
                filter: {
                  name: { equals: node.data.whatsappTemplateName },
                  customerId: { equals: user?.customerId },
                },
                select: {
                  id: true,
                  buttonUrlLink: true,
                  buttonUrlLink2: true,
                  urlButtonsInCards: true,
                },
              });

            type = "whatsapp_template";
            nodeType = "whatsappTemplate";

            const { limitedTimeOffer } = node.data;
            const {
              expirationDelayInMinutes,
              expirationType,
              expirationDate,
              text: limitedTimeOfferText,
            } = limitedTimeOffer || {};

            const shouldCreateRedirection =
              !officialWhatsappTemplate &&
              node.data?.urlButton &&
              node.data?.urlButton?.trackingEnabled !== false &&
              (!node.data?.urlButton?.url ||
                !node.data.urlButton.url.includes("{{1}}"));

            const shouldCreateRedirection2 =
              !officialWhatsappTemplate &&
              node?.data?.urlButton2 &&
              node.data?.urlButton2?.trackingEnabled !== false &&
              (!node.data?.urlButton2?.url ||
                !node.data.urlButton2.url.includes("{{1}}"));

            const whatsappFlowId = node.data.flowButton?.flowId
              ? await ensureWhatsappFlowLinked({
                  customerId: user?.customerId,
                  flowId: node.data.flowButton.flowId,
                })
              : null;

            nodeData = {
              customer: { _link: user?.customerId },
              whatsappTemplateName: node.data.whatsappTemplateName,
              headerParameters: node.data.headerParameters,
              bodyParameters: node.data.bodyParameters,
              buttonParameters: node.data.buttonParameters,
              isEditing: node.data.isEditing,
              isCancelable: node.data.isCancelable,
              copyCode: node.data.copyCodeButton?.text,
              isCopyCodeStatic: true,
              expirationDelayInMinutes,
              expirationType,
              expirationDate,
              limitedTimeOfferText,
              cards: node.data.cards,
              ...(whatsappFlowId && {
                whatsappFlow: { _link: whatsappFlowId },
              }),
              messageSkeleton: {
                create: {
                  customer: { _link: user?.customerId },
                  body: node.data.body,
                  footer: node.data.footer,
                  imageLink: node.data.headerParameters?.image?.link,
                  videoLink: node.data.headerParameters?.video?.link,
                  audioLink: node.data.headerParameters?.audio?.link,
                  campaign: { _link: campaignId },
                  isCampaignTrigger: node.data.isTemplate,
                  urlButtonText: node.data?.urlButton?.text,
                  urlButtonLink: urlButtonLink,
                  urlButtonText2: node.data?.urlButton2?.text,
                  urlButtonLink2: urlButtonLink2,
                  isUrlButtonAtBottom: node.data?.isUrlButtonAtBottom,
                  shopifyUpsellLineItems: node.data?.shopifyUpsellLineItems,
                },
              },

              ...(officialWhatsappTemplate?.buttonUrlLink && {
                redirectionUrl: {
                  create: {
                    customer: { _link: user?.customerId },
                    url: officialWhatsappTemplate.buttonUrlLink,
                    uuid: v4(),
                  },
                },
              }),
              ...(shouldCreateRedirection && {
                redirectionUrl: {
                  create: {
                    customer: { _link: user?.customerId },
                    url: node.data?.urlButton?.url,
                    uuid: v4(),
                  },
                },
              }),
              ...(officialWhatsappTemplate?.buttonUrlLink2 && {
                redirectionUrl2: {
                  create: {
                    customer: { _link: user?.customerId },
                    url: officialWhatsappTemplate.buttonUrlLink2,
                    uuid: v4(),
                  },
                },
              }),
              ...(shouldCreateRedirection2 && {
                redirectionUrl2: {
                  create: {
                    customer: { _link: user?.customerId },
                    url: node.data?.urlButton?.url,
                    uuid: v4(),
                  },
                },
              }),
            };
            console.log(
              `Create flow node type=${type} nodeType=${nodeType} nodeData=${JSON.stringify(nodeData)}`,
            );
            const flowNodeRecord = await api.flowNode.create(
              {
                flowNode: {
                  type,
                  customer: { _link: user?.customerId },
                  scenario: { _link: scenarioId },
                  positionX: Math.round(node.position.x),
                  positionY: Math.round(node.position.y),
                  [nodeType]: {
                    create: nodeData,
                  },
                },
              },
              {
                select: {
                  id: true,
                  messageSkeleton: { id: true },
                  whatsappTemplate: {
                    id: true,
                    messageSkeleton: { id: true },
                    redirectionUrl: { id: true },
                    redirectionUrl2: { id: true },
                  },
                  aiConfiguration: { id: true },
                  waitingNode: { id: true },
                },
              },
            );

            if (node.data?.cards) {
              const whatsappTemplateId = flowNodeRecord.whatsappTemplate.id;

              await processNodeCards(
                node,
                user,
                config,
                scenarioId,
                api,
                officialWhatsappTemplate,
                whatsappTemplateId,
                flowNodeRecord,
              );
            }

            // Link the redirections back to their flowNode - nested creation
            // under whatsappTemplate doesn't let Gadget infer this on its own.
            if (flowNodeRecord.whatsappTemplate?.redirectionUrl?.id) {
              await api.redirection.update(
                flowNodeRecord.whatsappTemplate.redirectionUrl.id,
                { flowNode: { _link: flowNodeRecord.id } },
              );
            }
            if (flowNodeRecord.whatsappTemplate?.redirectionUrl2?.id) {
              await api.redirection.update(
                flowNodeRecord.whatsappTemplate.redirectionUrl2.id,
                { flowNode: { _link: flowNodeRecord.id } },
              );
            }

            node.data.nodeId = flowNodeRecord.id;
            node.data.whatsappTemplateId = flowNodeRecord.whatsappTemplate.id;
            node.data.messageSkeletonId =
              flowNodeRecord.whatsappTemplate.messageSkeleton.id;

            // If campaign trigger
            if (node.data.isTemplate && flowNodeRecord) {
              console.log("update campaign trigger");
              node.data.isTemplate = true;
              if (flowNodeRecord?.whatsappTemplate?.id) {
                try {
                  await api.campaign.update(campaignId, {
                    whatsappTemplate: {
                      _link: flowNodeRecord.whatsappTemplate.id,
                    },
                  });
                  console.log("Campagne mise à jour avec succès");
                } catch (error) {
                  console.error(
                    "Erreur lors de la mise à jour de la campagne : ",
                    error,
                  );
                }
              } else {
                console.error(
                  "Erreur : whatsappTemplate ID est manquant ou invalide",
                );
              }
            }
            console.log(
              `Should try to create reply button flag=${!!node.data.buttons}`,
            );
            if (node.data.buttons.length > 0) {
              const buttons = node.data.buttons;

              for (let i = 0; i < buttons.length; i++) {
                const b = buttons[i];
                const customField = b.saveAsCustomField
                  ? { [b.customField]: b.customFieldValue }
                  : null;
                if ((b.gadgetId ?? "") === "") {
                  const id = v4();
                  b.whatsappId = id;
                  const replyButtonNode = await api.flowNode.create({
                    flowNode: {
                      customer: {
                        _link: user?.customerId,
                      },
                      type: "reply_button",
                      scenario: { _link: scenarioId },
                      parentNode: { _link: node.data.nodeId },
                      replyButton: {
                        create: {
                          messageSkeleton: {
                            _link: node.data.messageSkeletonId,
                          },
                          customer: {
                            _link: user?.customerId,
                          },
                          whatsappId: id,
                          title: b.title,
                          customField,
                        },
                      },
                    },
                  });
                  b.gadgetId = replyButtonNode.id;
                  const record = await api.flowNode.findById(
                    replyButtonNode.id,
                    {
                      select: { replyButton: { id: true } },
                    },
                  );
                  b.replyButtonGadgetId = record.replyButton.id;
                } else {
                  api.replyButton.update(b.replyButtonGadgetId, {
                    replyButton: {
                      messageSkeleton: { _link: node.data.messageSkeletonId },
                      title: b.title,
                      customField,
                    },
                  });
                }
              }
            }

            console.log(
              `Should try to create URL button flag=${!!node.data.urlButtons}`,
            );
            if (urlButtons.length > 0) {
              await processUrlButtons(
                urlButtons,
                node,
                user,
                config,
                api,
                flowNodeRecord,
                node.data.shouldBeReplaced,
              );
            }

            console.log(
              `Should try to create Flow button flag=${!!node.data.flowButton}`,
            );
            if (node.data.flowButton) {
              const flowButtonData = node.data.flowButton;
              console.log(
                `Trying to save flow button flowButtonData=${JSON.stringify(flowButtonData)}`,
              );

              let shouldCreateFlowButton = false;
              let existingFlowButtonNode = null;

              try {
                const existingFlowButtons = await api.flowNode.findMany({
                  filter: {
                    type: { equals: "flow_button" },
                    parentNodeId: { equals: node.data.nodeId },
                  },
                  select: {
                    id: true,
                    flowButton: { id: true, messageSkeletonId: true },
                  },
                });

                if (existingFlowButtons.length > 0) {
                  existingFlowButtonNode =
                    existingFlowButtons.find(
                      (fb) =>
                        fb.flowButton.messageSkeletonId ===
                        node.data.messageSkeletonId,
                    ) || existingFlowButtons[0];
                  console.log(
                    `Found existing flow_button ${existingFlowButtonNode.id} for parentNode ${node.data.nodeId} and messageSkeleton ${node.data.messageSkeletonId}`,
                  );
                  node.data.flowButton.gadgetId = existingFlowButtonNode.id;
                  node.data.flowButton.id =
                    existingFlowButtonNode.flowButton.id;
                  console.log(
                    `[FlowButton] Assigned existing gadgetId ${existingFlowButtonNode.id} to node ${node.id} (nodeId: ${node.data.nodeId})`,
                  );
                } else {
                  console.log(
                    `No existing flow_button found for parentNode ${node.data.nodeId}, will create new one`,
                  );
                  shouldCreateFlowButton = true;
                }
              } catch {
                console.log(
                  `Error checking existing flow_button, will create new one`,
                );
                shouldCreateFlowButton = true;
              }

              if (shouldCreateFlowButton) {
                const { flowAction, flowId, navigateScreen, text } =
                  flowButtonData;
                console.log(
                  `Saving flow button flowButtonData=${JSON.stringify(flowButtonData)}`,
                );
                try {
                  const flowButtonNode = await api.flowNode.create({
                    flowNode: {
                      customer: { _link: user?.customerId },
                      type: "flow_button",
                      scenario: { _link: scenarioId },
                      parentNode: { _link: node.data.nodeId },
                      flowButton: {
                        create: {
                          text,
                          flowAction,
                          flowId,
                          navigateScreen,
                          messageSkeleton: {
                            _link: node.data.messageSkeletonId,
                          },
                          customer: { _link: user?.customerId },
                        },
                      },
                    },
                  });

                  node.data.flowButton.gadgetId = flowButtonNode.id;
                  console.log(
                    `[FlowButton] Assigned gadgetId ${flowButtonNode.id} to node ${node.id} (nodeId: ${node.data.nodeId})`,
                  );

                  const record = await api.flowNode.findById(
                    flowButtonNode.id,
                    {
                      select: { flowButton: { id: true } },
                    },
                  );

                  node.data.flowButton.id = record.flowButton.id;
                  console.log(
                    `Flow button saved flowButtonData=${JSON.stringify(flowButtonData)}`,
                  );
                } catch (e) {
                  console.error(`Unable to save flow button ${e}`);
                }
              } else {
                console.log(
                  "Flow button already exists with correct messageSkeleton, no need to update it is not editable through Simio",
                );
              }
            }
          } else {
            if (!urlButtons[0]?.gadgetId || !urlButtons[1]?.gadgetId) {
              if (urlButtons.length > 0) {
                await processUrlButtons(
                  urlButtons,
                  node,
                  user,
                  config,
                  api,
                  null,
                  node.data.shouldBeReplaced,
                );
              }
            }

            console.log(
              `Trying to save flow button flag=${!!node.data.flowButton}`,
            );
            if (node.data.flowButton) {
              const flowButtonData = node.data.flowButton;
              console.log(
                `Trying to save flow button ${JSON.stringify(flowButtonData)}`,
              );

              let shouldCreateFlowButton = false;
              let existingFlowButtonNode = null;

              try {
                const existingFlowButtons = await api.flowNode.findMany({
                  filter: {
                    type: { equals: "flow_button" },
                    parentNodeId: { equals: node.data.nodeId },
                  },
                  select: {
                    id: true,
                    flowButton: { id: true, messageSkeletonId: true },
                  },
                });

                if (existingFlowButtons.length > 0) {
                  existingFlowButtonNode =
                    existingFlowButtons.find(
                      (fb) =>
                        fb.flowButton.messageSkeletonId ===
                        node.data.messageSkeletonId,
                    ) || existingFlowButtons[0];
                  console.log(
                    `Found existing flow_button ${existingFlowButtonNode.id} for parentNode ${node.data.nodeId} and messageSkeleton ${node.data.messageSkeletonId}`,
                  );
                  node.data.flowButton.gadgetId = existingFlowButtonNode.id;
                  node.data.flowButton.id =
                    existingFlowButtonNode.flowButton.id;
                  console.log(
                    `[FlowButton] Assigned existing gadgetId ${existingFlowButtonNode.id} to node ${node.id} (nodeId: ${node.data.nodeId})`,
                  );
                } else {
                  console.log(
                    `No existing flow_button found for parentNode ${node.data.nodeId}, will create new one`,
                  );
                  shouldCreateFlowButton = true;
                }
              } catch {
                console.log(
                  `Error checking existing flow_button, will create new one`,
                );
                shouldCreateFlowButton = true;
              }

              if (shouldCreateFlowButton) {
                const { flowAction, flowId, navigateScreen, text } =
                  flowButtonData;

                console.log(
                  `Saving flow button flowButtonData=${JSON.stringify(flowButtonData)}`,
                );
                try {
                  const flowButtonNode = await api.flowNode.create({
                    flowNode: {
                      customer: { _link: user?.customerId },
                      type: "flow_button",
                      scenario: { _link: scenarioId },
                      parentNode: { _link: node.data.nodeId },
                      flowButton: {
                        create: {
                          text,
                          flowAction,
                          flowId,
                          navigateScreen,
                          messageSkeleton: {
                            _link: node.data.messageSkeletonId,
                          },
                          customer: { _link: user?.customerId },
                        },
                      },
                    },
                  });

                  node.data.flowButton.gadgetId = flowButtonNode.id;

                  const record = await api.flowNode.findById(
                    flowButtonNode.id,
                    {
                      select: { flowButton: { id: true } },
                    },
                  );

                  node.data.flowButton.id = record.flowButton.id;
                  console.log(
                    `Flow button saved flowButtonData=${JSON.stringify(flowButtonData)}`,
                  );
                } catch (e) {
                  console.error(`Unable to save flow button ${e}`);
                }
              } else {
                console.log(
                  `Flow button already exists with correct parentNode and messageSkeleton, no need to update it`,
                );
              }
            }

            if (node.data.cards) {
              const hasMissingGadgetId = node.data.cards.some((card) =>
                card.components?.some(
                  (component) =>
                    component.type === "BUTTONS" &&
                    component.buttons?.some(
                      (button) =>
                        button.type === "URL" &&
                        (button.gadgetId === undefined ||
                          button.gadgetId === null ||
                          button.gadgetId === ""),
                    ),
                ),
              );

              if (hasMissingGadgetId) {
                let updatedCards = [];
                for (const [index, card] of node.data.cards.entries()) {
                  const buttonsCard = card.components.find(
                    (c) => c.type === "BUTTONS",
                  );
                  if (!buttonsCard) {
                    updatedCards.push(card);
                    continue;
                  }
                  const urlButtons = buttonsCard.buttons.filter(
                    (c) => c.type === "URL" || c.type === "url",
                  );
                  const officialWhatsappTemplate =
                    await api.officialWhatsappTemplate.maybeFindFirst({
                      filter: {
                        name: { equals: node.data.whatsappTemplateName },
                        customerId: {
                          equals: user?.customerId ?? config.customerId,
                        },
                      },
                      select: {
                        id: true,
                        urlButtonsInCards: true,
                      },
                    });
                  await processUrlButtonsInCards(
                    urlButtons,
                    index,
                    user,
                    config,
                    api,
                    officialWhatsappTemplate,
                    node.data.whatsappTemplateId,
                    node.data.nodeId,
                  );

                  updatedCards.push({ ...card });
                }

                node.data.cards = updatedCards;

                if (node.data.whatsappTemplateId) {
                  await api.whatsappTemplate.update(
                    node.data.whatsappTemplateId,
                    {
                      cards: updatedCards,
                    },
                  );
                }
              }
            }
            if (!node.data.isEditing) {
              node.data.oldData = null;
            }
            if (node.data.whatsappTemplateId) {
              const { limitedTimeOffer } = node.data;
              const {
                expirationDelayInMinutes,
                expirationType,
                expirationDate,
              } = limitedTimeOffer || {};
              api.whatsappTemplate.update(node.data.whatsappTemplateId, {
                headerParameters: node.data?.headerParameters,
                bodyParameters: node.data?.bodyParameters,
                copyCode: node.data?.copyCodeButton?.text || null,
                isEditing: node.data?.isEditing,
                expirationDelayInMinutes,
                expirationType,
                expirationDate,
                cards: node.data.cards,
              });
            }
            // Update message skeleton for template nodes to save shopifyUpsellLineItems
            if (node.data.messageSkeletonId) {
              api.messageSkeleton.update(node.data.messageSkeletonId, {
                messageSkeleton: {
                  shopifyUpsellLineItems: node.data?.shopifyUpsellLineItems,
                },
              });
            }
            if (node.data.nodeId) {
              api.flowNode.update(node.data.nodeId, {
                flowNode: {
                  positionX: Math.round(node.position.x),
                  positionY: Math.round(node.position.y),
                },
              });
            }
          }
          continue;
        } else if (node.type === "consent") {
          api.flowNode.update(node.data.nodeId, {
            flowNode: {
              positionX: Math.round(node.position.x),
              positionY: Math.round(node.position.y),
              isOptIn: node.data.type === "optin",
              isOptOut: node.data.type === "optout",
            },
          });
          continue;
        } else if (node.type === "wait") {
          nodeType = "waitingNode";
          nodeData = {
            id: node.data.waitingNodeId,
            type: node.data.type === "" ? null : node.data.type,
            durationInMinutes: parseInt(node.data.duration),
            messageStatus: "read",
            storeAnswer:
              node.data.type === "whatsapp_message"
                ? (node.data.storeAnswer ?? false)
                : null,
            storageField:
              node.data.type === "whatsapp_message"
                ? (node.data.storageField ?? null)
                : null,
          };
        } else if (node.type === "note") {
          api.flowNode.update(node.data.nodeId, {
            flowNode: {
              positionX: Math.round(node.position.x),
              positionY: Math.round(node.position.y),
              note: node.data.note,
            },
          });
          continue;
        } else if (node.type === "tag") {
          api.flowNode.update(node.data.nodeId, {
            flowNode: {
              positionX: Math.round(node.position.x),
              positionY: Math.round(node.position.y),
              isTag: true,
              tag: { _link: node.data.tagId },
            },
          });
          continue;
        } else if (node.type === "condition") {
          nodeType = "condition";
          nodeData = {
            id: node.data.conditionId,
            operator: node.data.operator ?? null,
            property: node.data.property ?? null,
            valueBoolean: node.data.valueBoolean ?? null,
            valueStartingPeriodInMinutes:
              parseInt(node.data.valueStartingPeriodInMinutes, 10) ?? null,
            valueType: node.data.valueType ?? null,
            valueTag: node.data.valueTag.id
              ? { _link: node.data.valueTag.id }
              : null,
            valuePercentage: node.data.valuePercentage ?? null,
            conditionFalseCount: node.data.falseCount,
            conditionTrueCount: node.data.trueCount,
          };
        } else if (node.type === "webhook") {
          nodeType = "webhookNode";
          nodeData = {
            id: node.data.webhookId,
            name: node.data.name ?? null,
            method: node.data.method === "" ? null : node.data.method,
            destinationUrl:
              node.data.destinationUrl.trim() === ""
                ? null
                : node.data.destinationUrl.trim().startsWith("http")
                  ? node.data.destinationUrl
                      .trim()
                      .replace(/^http:\/\//, "https://")
                  : `https://${node.data.destinationUrl.trim()}`,
            headers:
              node.data.headers.length === 1 && node.data.headers[0].key === ""
                ? null
                : node.data.headers,
            queryParams:
              node.data.queryParams.length === 1 &&
              node.data.queryParams[0].key === ""
                ? null
                : node.data.queryParams,
            pathParams:
              node.data.pathParams.length === 1 &&
              node.data.pathParams[0].key === ""
                ? null
                : node.data.pathParams,
            configResponse:
              node.data.configResponse.length === 1 &&
              node.data.configResponse[0].key === ""
                ? null
                : node.data.configResponse,
            body:
              node.data.body === "" ? null : forceJsonFormat(node.data.body),
          };
        } else if (node.type === "ai") {
          const _gaiaWebsiteDatasources = (
            node.data?.gaiaWebsiteDatasources || []
          ).filter(({ url }) => url !== "");
          if (
            !gaiaWebsiteDatasourcesModified &&
            _gaiaWebsiteDatasources?.length
          ) {
            const gaiaWebsiteDatasources =
              await api.generateGaiaWebsiteDatasource({
                gaiaWebsiteDatasources: _gaiaWebsiteDatasources,
                title: `${user.customer.name} - chatbot`,
              });
            await api.customer.update(user?.customerId, {
              gaiaWebsiteDatasources,
            });
            gaiaWebsiteDatasourcesModified = true;
          }

          nodeType = "aiConfiguration";
          nodeData = {
            id: node.data.aiConfigurationId,
            answerStyle: node.data.answerStyle,
            assistantContext: node.data.assistantContext,
            companyContext: node.data.companyContext,
          };
        }

        if (node.type !== "automationTrigger") {
          api.flowNode.update(node.data.nodeId, {
            flowNode: {
              positionX: Math.round(node.position.x),
              positionY: Math.round(node.position.y),
              [nodeType]: {
                update: nodeData,
              },
            },
          });
        }
      } else {
        let type;
        let nodeType;
        let nodeData;

        if (node.type === "trigger") {
          type = "whatsapp_trigger";
          nodeType = "whatsappTrigger";
          nodeData = {
            id: node.data.whatsappTriggerId,
            type: node.data.type,
            text: node.data.text.trim(),
          };
        } else if (node.type === "message") {
          type = "message_skeleton";
          nodeType = "messageSkeleton";
          nodeData = _formatNodeData(node, { urlButtonLink, urlButtonLink2 });
        } else if (node.type === "template") {
          const officialWhatsappTemplate =
            await api.officialWhatsappTemplate.maybeFindFirst({
              filter: {
                name: { equals: node.data.whatsappTemplateName },
                customerId: { equals: user?.customerId },
              },
              select: {
                id: true,
                buttonUrlLink: true,
                buttonUrlLink2: true,
              },
            });

          type = "whatsapp_template";
          nodeType = "whatsappTemplate";
          const { limitedTimeOffer } = node.data;
          const {
            expirationDelayInMinutes,
            expirationType,
            expirationDate,
            text: limitedTimeOfferText,
          } = limitedTimeOffer || {};

          const shouldCreateRedirection =
            !officialWhatsappTemplate &&
            node.data?.urlButton &&
            node.data?.urlButton?.trackingEnabled !== false &&
            (!node.data?.urlButton?.url ||
              !node.data.urlButton.url.includes("{{1}}"));

          const shouldCreateRedirection2 =
            !officialWhatsappTemplate &&
            node?.data?.urlButton2 &&
            node.data?.urlButton2?.trackingEnabled !== false &&
            (!node.data?.urlButton2?.url ||
              !node.data.urlButton2.url.includes("{{1}}"));

          const whatsappFlowId = node.data.flowButton?.flowId
            ? await ensureWhatsappFlowLinked({
                customerId: user?.customerId,
                flowId: node.data.flowButton.flowId,
              })
            : null;

          nodeData = {
            customer: { _link: user?.customerId },
            whatsappTemplateName: node.data.whatsappTemplateName,
            headerParameters: node.data.headerParameters,
            bodyParameters: node.data.bodyParameters,
            buttonParameters: node.data.buttonParameters,
            isEditing: node.data.isEditing,
            copyCode: node.data.copyCodeButton?.text,
            isCopyCodeStatic: true,
            expirationDelayInMinutes,
            expirationType,
            expirationDate,
            limitedTimeOfferText,
            cards: node.data.cards,
            ...(whatsappFlowId && { whatsappFlow: { _link: whatsappFlowId } }),
            messageSkeleton: {
              create: {
                customer: { _link: user?.customerId },
                body: node.data.body,
                footer: node.data.footer,
                imageLink: node.data.headerParameters?.image?.link,
                videoLink: node.data.headerParameters?.video?.link,
                audioLink: node.data.headerParameters?.audio?.link,
                campaign: { _link: campaignId },
                isCampaignTrigger: false,
                urlButtonText: node.data?.urlButton?.text,
                urlButtonLink: urlButtonLink,
                urlButtonText2: node.data?.urlButton2?.text,
                urlButtonLink2: urlButtonLink2,
                isUrlButtonAtBottom: node.data?.isUrlButtonAtBottom,
                shopifyUpsellLineItems: node.data?.shopifyUpsellLineItems,
              },
            },
            ...(officialWhatsappTemplate?.buttonUrlLink && {
              redirectionUrl: {
                create: {
                  customer: { _link: user?.customerId },
                  url: officialWhatsappTemplate.buttonUrlLink,
                  uuid: v4(),
                },
              },
            }),
            ...(shouldCreateRedirection && {
              redirectionUrl: {
                create: {
                  customer: { _link: user?.customerId },
                  url: node.data?.urlButton?.url,
                  uuid: v4(),
                },
              },
            }),
            ...(officialWhatsappTemplate?.buttonUrlLink2 && {
              redirectionUrl2: {
                create: {
                  customer: { _link: user?.customerId },
                  url: officialWhatsappTemplate.buttonUrlLink2,
                  uuid: v4(),
                },
              },
            }),
            ...(shouldCreateRedirection2 && {
              redirectionUrl2: {
                create: {
                  customer: { _link: user?.customerId },
                  url: node.data?.urlButton2?.url,
                  uuid: v4(),
                },
              },
            }),
          };
        } else if (node.type === "consent") {
          const flowNodeRecord = await api.flowNode.create({
            flowNode: {
              type: "consent",
              customer: { _link: user?.customerId },
              scenario: { _link: scenarioId },
              positionX: Math.round(node.position.x),
              positionY: Math.round(node.position.y),
              isOptIn: node.data.type === "optin",
              isOptOut: node.data.type === "optout",
            },
          });
          node.data.nodeId = flowNodeRecord.id;
          continue;
        } else if (node.type === "wait") {
          type = "waiting_node";
          nodeType = "waitingNode";
          nodeData = {
            customer: { _link: user?.customerId },
            type: node.data.type === "" ? null : node.data.type,
            durationInMinutes: parseInt(node.data.duration),
            messageStatus: "read",
            storeAnswer:
              node.data.type === "whatsapp_message"
                ? (node.data.storeAnswer ?? false)
                : null,
            storageField:
              node.data.type === "whatsapp_message"
                ? (node.data.storageField ?? null)
                : null,
          };
        } else if (node.type === "note") {
          const flowNodeRecord = await api.flowNode.create({
            flowNode: {
              type: "note",
              customer: { _link: user?.customerId },
              scenario: { _link: scenarioId },
              positionX: Math.round(node.position.x),
              positionY: Math.round(node.position.y),
              note: node.data.note,
            },
          });
          node.data.nodeId = flowNodeRecord.id;
          continue;
        } else if (node.type === "condition") {
          type = "condition";
          nodeType = "condition";
          nodeData = {
            customer: { _link: user?.customerId },
            operator: node.data.operator ?? null,
            property: node.data.property ?? null,
            valueBoolean: node.data.valueBoolean ?? null,
            valueStartingPeriodInMinutes:
              parseInt(node.data.valueStartingPeriodInMinutes, 10) ?? null,
            valueType: node.data.valueType ?? null,
            valueTag: node.data.valueTag?.id
              ? { _link: node.data.valueTag?.id }
              : null,
            valuePercentage: node.data.valuePercentage ?? null,
          };
        } else if (node.type === "webhook") {
          type = "webhook_node";
          nodeType = "webhookNode";
          nodeData = {
            customer: { _link: user?.customerId },
            name: node.data.name ?? null,
            method: node.data.method === "" ? null : node.data.method,
            destinationUrl:
              node.data.destinationUrl.trim() === ""
                ? null
                : node.data.destinationUrl.trim().startsWith("https://")
                  ? node.data.destinationUrl.trim()
                  : `https://${node.data.destinationUrl.trim()}`,
            headers: node.data.headers === "" ? null : node.data.headers,
            queryParams:
              node.data.queryParams === "" ? null : node.data.queryParams,
            pathParams:
              node.data.pathParams === "" ? null : node.data.pathParams,
            configResponse:
              node.data.configResponse === "" ? null : node.data.configResponse,
            body:
              node.data.body === "" ? null : forceJsonFormat(node.data.body),
          };
        } else if (node.type === "tag") {
          const flowNodeRecord = await api.flowNode.create({
            flowNode: {
              type: "tag",
              customer: { _link: user?.customerId },
              scenario: { _link: scenarioId },
              positionX: Math.round(node.position.x),
              positionY: Math.round(node.position.y),
              isTag: true,
              tag: { _link: node.data.tagId },
            },
          });
          node.data.nodeId = flowNodeRecord.id;
          continue;
        } else if (node.type === "ai") {
          type = "ai";
          nodeType = "aiConfiguration";
          nodeData = {
            customer: { _link: user?.customerId },
            answerStyle: node.data.answerStyle,
            assistantContext: node.data.assistantContext,
            companyContext: node.data.companyContext,
          };
        }

        const flowNodeRecord = await api.flowNode.create(
          {
            flowNode: {
              type,
              customer: { _link: user?.customerId },
              scenario: { _link: scenarioId },
              positionX: Math.round(node.position.x),
              positionY: Math.round(node.position.y),
              [nodeType]: {
                create: nodeData,
              },
            },
          },
          {
            select: {
              id: true,
              messageSkeleton: { id: true, redirectionUrl: { id: true } },
              whatsappTemplate: {
                id: true,
                messageSkeleton: { id: true },
                redirectionUrl: { id: true },
                redirectionUrl2: { id: true },
              },
              aiConfiguration: { id: true },
              waitingNode: { id: true },
              condition: { id: true },
              webhookNode: { id: true },
            },
          },
        );
        node.data.nodeId = flowNodeRecord.id;

        if (node.data?.urlButton) {
          urlButtons.push(node.data?.urlButton);
        }
        if (node.data?.urlButton2) {
          urlButtons.push(node.data?.urlButton2);
        }

        //changer cette condition pour ajouter un flowNode sur les url_Buttons dans les messages
        if (urlButtons.length > 0 && node.type === "template") {
          processUrlButtons(
            urlButtons,
            node,
            user,
            config,
            api,
            flowNodeRecord,
            node.data.shouldBeReplaced,
          );
        }

        if (node.type === "message") {
          node.data.messageSkeletonId = flowNodeRecord.messageSkeleton.id;
          // Update redirectionUrlId if redirection was created
          if (
            flowNodeRecord.messageSkeleton?.redirectionUrl?.id &&
            node.data?.urlButton
          ) {
            node.data.urlButton.redirectionUrlId =
              flowNodeRecord.messageSkeleton.redirectionUrl.id;
          }
          // Link the redirection back to its flowNode - nested creation under
          // messageSkeleton doesn't let Gadget infer this link on its own.
          if (flowNodeRecord.messageSkeleton?.redirectionUrl?.id) {
            await api.redirection.update(
              flowNodeRecord.messageSkeleton.redirectionUrl.id,
              { flowNode: { _link: flowNodeRecord.id } },
            );
          }
        } else if (node.type === "template") {
          node.data.messageSkeletonId =
            flowNodeRecord.whatsappTemplate.messageSkeleton.id;
          // Update redirectionUrlId if redirection was created
          if (
            flowNodeRecord.whatsappTemplate?.redirectionUrl?.id &&
            node.data?.urlButton
          ) {
            node.data.urlButton.redirectionUrlId =
              flowNodeRecord.whatsappTemplate.redirectionUrl.id;
          }
          if (
            flowNodeRecord.whatsappTemplate?.redirectionUrl2?.id &&
            node.data?.urlButton2
          ) {
            node.data.urlButton2.redirectionUrlId =
              flowNodeRecord.whatsappTemplate.redirectionUrl2.id;
          }
          // Link the redirections back to their flowNode - nested creation
          // under whatsappTemplate doesn't let Gadget infer this on its own.
          if (flowNodeRecord.whatsappTemplate?.redirectionUrl?.id) {
            await api.redirection.update(
              flowNodeRecord.whatsappTemplate.redirectionUrl.id,
              { flowNode: { _link: flowNodeRecord.id } },
            );
          }
          if (flowNodeRecord.whatsappTemplate?.redirectionUrl2?.id) {
            await api.redirection.update(
              flowNodeRecord.whatsappTemplate.redirectionUrl2.id,
              { flowNode: { _link: flowNodeRecord.id } },
            );
          }

          if (node.data?.cards) {
            const whatsappTemplateId = flowNodeRecord.whatsappTemplate.id;
            node.data.whatsappTemplateId = flowNodeRecord.whatsappTemplate.id;
            const officialWhatsappTemplate =
              await api.officialWhatsappTemplate.maybeFindFirst({
                filter: {
                  name: { equals: node.data.whatsappTemplateName },
                  customerId: { equals: user?.customerId },
                },
                select: {
                  id: true,
                  urlButtonsInCards: true,
                },
              });
            await processNodeCards(
              node,
              user,
              config,
              scenarioId,
              api,
              officialWhatsappTemplate,
              whatsappTemplateId,
              flowNodeRecord,
            );
          }
        } else if (node.type === "wait") {
          node.data.waitingNodeId = flowNodeRecord.waitingNode.id;
        } else if (node.type === "ai") {
          node.data.aiConfigurationId = flowNodeRecord.aiConfiguration.id;
        } else if (node.type === "condition") {
          node.data.conditionId = flowNodeRecord.condition.id;
        } else if (node.type === "webhook") {
          node.data.webhookId = flowNodeRecord.webhookNode.id;
        }
      }

      if (node.type === "message" || node.type === "template") {
        const buttons = node.data?.buttons || [];

        if (node.data?.flowButton) {
          if (!node.data.flowButton.gadgetId) {
            let shouldCreateFlowButton = false;
            let existingFlowButtonNode = null;

            try {
              const existingFlowButtons = await api.flowNode.findMany({
                filter: {
                  type: { equals: "flow_button" },
                  parentNodeId: { equals: node.data.nodeId },
                },
                select: {
                  id: true,
                  flowButton: { id: true, messageSkeletonId: true },
                },
              });

              if (existingFlowButtons.length > 0) {
                existingFlowButtonNode =
                  existingFlowButtons.find(
                    (fb) =>
                      fb.flowButton.messageSkeletonId ===
                      node.data.messageSkeletonId,
                  ) || existingFlowButtons[0];
                console.log(
                  `[FlowButton] Found existing flow_button ${existingFlowButtonNode.id} for parentNode ${node.data.nodeId} and messageSkeleton ${node.data.messageSkeletonId}`,
                );
                node.data.flowButton.gadgetId = existingFlowButtonNode.id;
                node.data.flowButton.id = existingFlowButtonNode.flowButton.id;
                console.log(
                  `[FlowButton] Assigned existing gadgetId ${existingFlowButtonNode.id} to node ${node.id} (nodeId: ${node.data.nodeId})`,
                );
              } else {
                shouldCreateFlowButton = true;
              }
            } catch {
              shouldCreateFlowButton = true;
            }

            if (shouldCreateFlowButton) {
              const { flowAction, flowId, navigateScreen, text } =
                node.data.flowButton;
              console.log(
                `Creating flow button for new template flowButtonData=${JSON.stringify(node.data.flowButton)}`,
              );
              try {
                const flowButtonNode = await api.flowNode.create({
                  flowNode: {
                    customer: { _link: user?.customerId },
                    type: "flow_button",
                    scenario: { _link: scenarioId },
                    parentNode: { _link: node.data.nodeId },
                    flowButton: {
                      create: {
                        text,
                        flowAction,
                        flowId,
                        navigateScreen,
                        messageSkeleton: {
                          _link: node.data.messageSkeletonId,
                        },
                        customer: { _link: user?.customerId },
                      },
                    },
                  },
                });

                node.data.flowButton.gadgetId = flowButtonNode.id;
                console.log(
                  `[FlowButton] Assigned gadgetId ${flowButtonNode.id} to node ${node.id} (nodeId: ${node.data.nodeId})`,
                );

                const record = await api.flowNode.findById(flowButtonNode.id, {
                  select: { flowButton: { id: true } },
                });

                node.data.flowButton.id = record.flowButton.id;
                console.log(
                  `Flow button created for new template flowButtonData=${JSON.stringify(node.data.flowButton)}`,
                );
              } catch (e) {
                console.error(`Unable to create flow button ${e}`);
              }
            }
          }
        }

        if (node.data?.type === "list") {
          // Interactive reply list
          for (let i = 0; i < buttons.length; i++) {
            const b = buttons[i];
            const customField = b.saveAsCustomField
              ? { [b.customField]: b.customFieldValue }
              : null;
            if ((b.gadgetId ?? "") === "") {
              const id = v4();
              b.whatsappId = id;
              const replyListNode = await api.flowNode.create({
                flowNode: {
                  customer: { _link: user?.customerId },
                  type: "reply_list",
                  scenario: { _link: scenarioId },
                  parentNode: { _link: node.data.nodeId },
                  replyList: {
                    create: {
                      messageSkeleton: { _link: node.data.messageSkeletonId },
                      customer: {
                        _link: user?.customerId,
                      },
                      whatsappId: id,
                      title: b.title,
                      description: b?.description,
                      customField,
                    },
                  },
                },
              });
              b.gadgetId = replyListNode.id;
              const record = await api.flowNode.findById(replyListNode.id, {
                select: { replyList: { id: true } },
              });
              b.replyListGadgetId = record.replyList.id;
            } else {
              api.replyList.update(b.replyListGadgetId, {
                replyList: {
                  messageSkeleton: { _link: node.data.messageSkeletonId },
                  title: b.title,
                  description: b?.description,
                  customField,
                },
              });
            }
          }
        }

        // Interactive reply buttons
        else {
          for (let i = 0; i < buttons.length; i++) {
            const b = buttons[i];
            const customField = b.saveAsCustomField
              ? { [b.customField]: b.customFieldValue }
              : null;
            if ((b.gadgetId ?? "") === "") {
              const id = v4();
              b.whatsappId = id;
              const replyButtonNode = await api.flowNode.create({
                flowNode: {
                  customer: { _link: user?.customerId },
                  type: "reply_button",
                  scenario: { _link: scenarioId },
                  parentNode: { _link: node.data.nodeId },
                  replyButton: {
                    create: {
                      messageSkeleton: { _link: node.data.messageSkeletonId },
                      customer: {
                        _link: user?.customerId,
                      },
                      whatsappId: id,
                      title: b.title,
                      customField,
                    },
                  },
                },
              });
              b.gadgetId = replyButtonNode.id;
              const record = await api.flowNode.findById(replyButtonNode.id, {
                select: { replyButton: { id: true } },
              });
              b.replyButtonGadgetId = record.replyButton.id;
            } else {
              api.replyButton.update(b.replyButtonGadgetId, {
                replyButton: {
                  messageSkeleton: { _link: node.data.messageSkeletonId },
                  title: b.title,
                  customField,
                },
              });
            }
          }
        }
      }
    }
  }

  const invalidSourceHandleForQuickReply = new Set([
    null,
    undefined,
    "",
    "main",
    "true",
    "false",
  ]);

  async function edgeLoop() {
    console.log("edges before loop", JSON.stringify(edges));
    const handleIndexMapping = [
      "a",
      "b",
      "c",
      "d",
      "e",
      "f",
      "g",
      "h",
      "i",
      "j",
      "k",
      "l",
      "m",
      "n",
      "o",
      "p",
      "q",
      "r",
      "s",
      "t",
    ];

    console.log("[Edge Processing] Starting edge processing");
    console.log(
      "[Edge Processing] Nodes with flowButton:",
      nodes
        .filter((n) => n.data?.flowButton)
        .map((n) => ({
          nodeId: n.id,
          nodeDataId: n.data.nodeId,
          hasGadgetId: !!n.data.flowButton.gadgetId,
          gadgetId: n.data.flowButton.gadgetId,
        })),
    );

    console.log(
      "[Edge Processing] All edges:",
      edges.map((e) => ({
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle,
        edgeId: e.data?.edgeId,
      })),
    );

    for (var k = 0; k < edges.length; k++) {
      const edge = edges[k];
      const sourceNode = nodes.find((n) => n.id === edge.source);
      const targetNode = nodes.find((n) => n.id === edge.target);
      let condition = null;

      var sourceId = sourceNode.data.nodeId;
      var targetId = targetNode.data.nodeId;

      if (edge.sourceHandle === "flowButton") {
        console.log(
          `[Edge Debug] Processing flowButton edge - edge.source: ${edge.source}, sourceNode found: ${!!sourceNode}, sourceNode.id: ${sourceNode?.id}, hasFlowButton: ${!!sourceNode?.data?.flowButton}, hasGadgetId: ${!!sourceNode?.data?.flowButton?.gadgetId}, gadgetId: ${sourceNode?.data?.flowButton?.gadgetId}`,
        );
        if (sourceNode?.data?.flowButton) {
          console.log(
            `[Edge Debug] FlowButton data:`,
            JSON.stringify(sourceNode.data.flowButton),
          );
        }
      }

      // Reply buttons
      if (!invalidSourceHandleForQuickReply.has(edge.sourceHandle)) {
        if (sourceNode.data.cards && Array.isArray(sourceNode.data.cards)) {
          let foundGadgetId = null;
          for (let i = 0; i < sourceNode.data.cards.length; i++) {
            const card = sourceNode.data.cards[i];
            const buttonsComponent = card.components.find(
              (c) => c.type === "BUTTONS",
            );
            if (buttonsComponent && Array.isArray(buttonsComponent.buttons)) {
              const globalIndex = handleIndexMapping.indexOf(edge.sourceHandle);
              const cardIdx = Math.floor(globalIndex / 2);
              if (i === cardIdx) {
                const btnIdx = globalIndex % 2;
                if (buttonsComponent.buttons[btnIdx]?.gadgetId) {
                  foundGadgetId = buttonsComponent.buttons[btnIdx].gadgetId;
                }
              }
            }
          }
          if (foundGadgetId) {
            sourceId = foundGadgetId;
          }
        } else if (
          sourceNode.data.buttons &&
          Array.isArray(sourceNode.data.buttons) &&
          edge.sourceHandle !== "flowButton" &&
          edge.sourceHandle !== "urlA" &&
          edge.sourceHandle !== "urlB"
        ) {
          const buttonIndex = handleIndexMapping.indexOf(edge.sourceHandle);
          if (sourceNode.data.buttons[buttonIndex]?.gadgetId) {
            sourceId = sourceNode.data.buttons[buttonIndex]?.gadgetId;
          }
        } else if (
          edge.sourceHandle === "urlA" &&
          sourceNode?.data?.urlButton?.gadgetId
        ) {
          sourceId = sourceNode.data.urlButton.gadgetId;
        } else if (
          edge.sourceHandle === "urlB" &&
          sourceNode?.data?.urlButton2?.gadgetId
        ) {
          sourceId = sourceNode.data.urlButton2.gadgetId;
        } else if (
          edge.sourceHandle === "flowButton" &&
          sourceNode?.data?.flowButton?.gadgetId
        ) {
          console.log(
            `[Edge] flowButton edge detected - sourceNode: ${sourceNode.id}, gadgetId: ${sourceNode.data.flowButton.gadgetId}, nodeId: ${sourceNode.data.nodeId}`,
          );
          sourceId = sourceNode.data.flowButton.gadgetId;
        } else if (sourceNode.data.buttons) {
          const buttonIndex = handleIndexMapping.indexOf(edge.sourceHandle);
          if (sourceNode.data.buttons[buttonIndex]?.gadgetId) {
            sourceId = sourceNode.data.buttons[buttonIndex].gadgetId;
          }
        }
      } else if (
        edge.sourceHandle === "true" ||
        edge.sourceHandle === "false"
      ) {
        condition = edge.sourceHandle === "true" ? true : false;
      }

      const isNoAnswer = edge.sourceHandle === "noAnswer";

      console.log("saving edges");

      if ((edge?.data?.edgeId ?? "") != "") {
        try {
          await api.flowEdge.update(edge.data.edgeId, {
            flowEdge: {
              sourceNode: { _link: sourceId },
              targetNode: { _link: targetId },
              isSourceTrueCondition: condition,
              isSourceFalseCondition: condition === null ? null : !condition,
              isSourceAiNoAnswer: isNoAnswer || null,
            },
          });
        } catch (e) {
          console.log("erreur save edge", e);
          const existingEdge = await api.flowEdge.maybeFindFirst({
            filter: {
              scenarioId: { equals: scenarioId },
              sourceNodeId: { equals: sourceId },
              targetNodeId: { equals: targetId },
            },
            select: {
              id: true,
            },
          });
          if (!existingEdge) {
            const flowEdgeRecord = await api.flowEdge.create({
              flowEdge: {
                scenario: { _link: scenarioId },
                sourceNode: { _link: sourceId },
                targetNode: { _link: targetId },
                isSourceTrueCondition: condition,
                isSourceFalseCondition: condition === null ? null : !condition,
                isSourceAiNoAnswer: isNoAnswer || null,
              },
            });
            edge.data = { edgeId: flowEdgeRecord.id };
          } else {
            console.log("edge already exists", existingEdge);
          }
        }
      }

      // New edge
      else {
        const existingEdge = await api.flowEdge.maybeFindFirst({
          filter: {
            scenarioId: { equals: scenarioId },
            sourceNodeId: { equals: sourceId },
            targetNodeId: { equals: targetId },
          },
          select: {
            id: true,
          },
        });
        if (!existingEdge) {
          const flowEdgeRecord = await api.flowEdge.create({
            flowEdge: {
              scenario: { _link: scenarioId },
              sourceNode: { _link: sourceId },
              targetNode: { _link: targetId },
              isSourceTrueCondition: condition,
              isSourceFalseCondition: condition === null ? null : !condition,
              isSourceAiNoAnswer: isNoAnswer || null,
            },
          });
          edge.data = { edgeId: flowEdgeRecord.id };
        } else {
          console.log("edge already exists", existingEdge);
        }
      }
    }
  }

  const _formatURLButtons = (nodeData, customer) => {
    if (nodeData) {
      const { urlButton, urlButton2 } = nodeData;
      const customFieldNames = Object.keys(customer?.customFields || {});
      const regex = /\{\{([^}]+)\}\}/g;

      let urlButtonLink;
      if (urlButton?.url) {
        urlButtonLink = urlButton.url.trim().startsWith("http")
          ? urlButton.url.trim()
          : `http://${urlButton.url.trim()}`;
        urlButtonLink = urlButtonLink.replace(regex, (match, fieldName) => {
          if (customFieldNames.includes(fieldName)) {
            return match;
          }
          return "{{1}}";
        });
      }

      let urlButtonLink2;
      if (urlButton2?.url) {
        urlButtonLink2 = urlButton2.url.trim().startsWith("http")
          ? urlButton2.url.trim()
          : `http://${urlButton2.url.trim()}`;
        urlButtonLink2 = urlButtonLink2.replace(regex, (match, fieldName) => {
          if (customFieldNames.includes(fieldName)) {
            return match;
          }
          return "{{1}}";
        });
      }

      return { urlButtonLink, urlButtonLink2 };
    }
    return { urlButtonLink: null, urlButtonLink2: null };
  };

  const _formatNodeData = (nodeItem, common, options) => {
    const { type: nodeType, data } = nodeItem;
    switch (nodeType) {
      case "message": {
        return _formatMessageNodeData(data, common, options);
      }
      default:
        return null;
    }
  };

  const _formatMessageNodeData = (data, common, options) => {
    const { urlButtonLink } = common ?? {};
    const {
      type,
      messageSkeletonId,
      nodeId,
      image,
      video,
      audio,
      sticker,
      document,
      documentName,
      body,
      bodyParameters,
      header,
      footer,
      buttonTitle,
      urlButton,
      shopifyUpsellLineItems,
    } = data;

    const nodeTypeMedia = type === "media";
    const nodeTypeList = type === "list";

    const nodeData = {
      imageLink: (nodeTypeMedia && image) || null,
      videoLink: (nodeTypeMedia && video) || null,
      audioLink: (nodeTypeMedia && audio) || null,
      documentLink: (nodeTypeMedia && document) || null,
      documentName: (nodeTypeMedia && documentName) || null,
      body: ((nodeTypeMedia || nodeTypeList) && body) || null,
      bodyParameters: bodyParameters || null,
      header: (nodeTypeList && header) || null,
      footer: footer || null,
      buttonTitle: (nodeTypeList && buttonTitle) || null,
      urlButtonText: (nodeTypeMedia && urlButton?.text) || null,
      urlButtonLink: (nodeTypeMedia && urlButtonLink) || null,
      shopifyUpsellLineItems: shopifyUpsellLineItems ?? null,
      redirectionUrl:
        nodeTypeMedia && urlButtonLink && urlButton?.trackingEnabled !== false
          ? {
              create: {
                customer: { _link: user?.customerId },
                url: urlButtonLink,
                uuid: v4(),
                // Existing node - its flowNode id is already known, so link it
                // directly instead of relying on nested-create inference
                // (which only works one relation hop deep).
                ...(options?.update && nodeId && { flowNode: { _link: nodeId } }),
              },
            }
          : null,
    };

    if (options?.update) {
      nodeData.id = messageSkeletonId;
      nodeData.stickerLink = sticker ?? null;
    }
    return nodeData;
  };

  await nodeLoop();
  await edgeLoop();

  await api.scenario.update(scenarioId, {
    savedAt: new Date(),
    name: campaignName,
  });

  if (campaignId) {
    try {
      await api.campaign.update(campaignId, {
        name: campaignName,
      });
    } catch (e) {
      console.error(`Error updating campaign: ${e}`);
    }
  }
}
